import React, { ComponentProps, FC, MutableRefObject, useCallback, useEffect, useMemo, useState } from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import useClassNames from '@loomstateorg/shared-frontend/hooks/useClassNames';
import Typography from '@loomstateorg/shared-frontend/components/commercetools-ui/atoms/typography';

export interface InputProps extends Omit<ComponentProps<'input'>, 'onChange'> {
  label?: string;
  labelDesc?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  variant?: 'primary' | 'secondary';
  labelPosition?: 'top' | 'inline';
  innerRef?: MutableRefObject<HTMLInputElement>;
  error?: string;
  errorMessage?: string;
  isValid?: boolean;
  hideCheckIcon?: boolean;
  validation?: (valueToValidate: string) => boolean;
  renderEndIcon?: () => React.ReactNode;
}

const Input: FC<InputProps> = ({
  label,
  labelDesc,
  onChange,
  onBlur,
  onFocus,
  variant = 'primary',
  labelPosition = 'top',
  className = '',
  innerRef,
  value,
  errorMessage,
  validation,
  children,
  hideCheckIcon,
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isTouched, setIsTouched] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [isErrored, setIsErrored] = useState(false);
  const [isEdited, setIsEdited] = useState(false);

  useEffect(() => {
    if (validation) {
      const shouldBeValidated = isTouched && isEdited;
      const validated = validation?.(value as string);
      setIsValid(shouldBeValidated && !!validated);
      setIsErrored(shouldBeValidated && !validated);
    }
  }, [isEdited, isTouched, validation, value]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(event);
      setIsEdited(true);
    },
    [onChange],
  );

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    },
    [onFocus],
  );
  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      setIsTouched(true);
      onBlur?.(e);
    },
    [onBlur],
  );

  const bgClassName = useMemo(
    () =>
      ({
        primary: 'bg-site',
        secondary: 'bg-neutral-200',
      })[variant],
    [variant],
  );

  const isInActiveState = useMemo(() => isFocused || !!value, [isFocused, value]);

  const labelClassName = useClassNames([
    { ['font-medium']: labelPosition === 'top' },
    { [isInActiveState && label ? 'opacity-1 scale-100' : 'scale-0 opacity-0']: labelPosition === 'inline' },
    {
      ['absolute top-[6px] left-[12px] font-medium block transition duration-150 ease-out']: labelPosition === 'inline',
    },
  ]);

  const labelContainerClassName = useClassNames([
    {
      ' mb-8': labelPosition === 'top',
    },
  ]);

  const inputClassName = useClassNames([
    'font-primaryThin text-14 h-40 focus:border-neutral-400 focus:ring-0 w-full rounded-lg border px-12 placeholder:text-14 placeholder:leading-normal focus:outline-none disabled:cursor-not-allowed disabled:bg-neutral-400',
    bgClassName,
    isInActiveState && label && labelPosition == 'inline' ? 'pt-[20px] pb-[4px]' : 'py-10',
    {
      'border-red-500 focus:border-red-500':
        (!!props.error && isInActiveState) ||
        isErrored ||
        (typeof props.isValid !== 'undefined' && !props.isValid && isTouched),
    },
    { 'border-green-500 focus:border-green-500': (!!props.isValid && isInActiveState) || isValid },
    className,
  ]);

  return (
    <div className="relative" data-testid="atom-input">
      <div className={labelContainerClassName}>
        {label && (
          <Typography
            lineHeight={labelPosition ? 'loose' : 'normal'}
            fontSize={labelPosition === 'top' ? 14 : 10}
            medium
            className={labelClassName}
          >
            {props.required ? `${label} *` : label}
          </Typography>
        )}
        {labelDesc && (
          <Typography
            lineHeight={labelPosition ? 'loose' : 'normal'}
            fontSize={labelPosition === 'top' ? 14 : 10}
            fontFamily="primaryThin"
            as="label"
            className={labelClassName}
          >
            {` (${labelDesc})`}
          </Typography>
        )}
      </div>
      <div className="relative">
        <input
          className={inputClassName}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          ref={innerRef}
          value={value}
          data-testid="atom-input-field"
          {...props}
        />
        {(isValid || (props.isValid && !isInActiveState)) && !isFocused && !hideCheckIcon && (
          <CheckIcon className="absolute right-12 top-[50%] size-16 translate-y-[-50%] text-green-500" />
        )}
        {children}
      </div>
      {(isErrored || props.error) && (
        <Typography className="mt-12 leading-[16px] text-red-500" medium fontSize={12} as="span">
          {props.error ?? errorMessage}
        </Typography>
      )}
    </div>
  );
};

export default Input;
